/**
 * Page copy for /industries.
 *
 * Source: page-industries-hub.md, supplied by the client.
 *
 * Scope decisions taken with the client:
 *
 * 1. SCOPE. This page lists five industries. The header dropdown and the footer
 *    now match it. The approved homepage Industries section still carries the
 *    original three (FinTech & Financial Services, Healthcare & Pharma,
 *    eCommerce & Retail) with its tabs unlinked — that section is approved
 *    design copy and has not been re-scoped, so the homepage and the nav
 *    disagree until the client rewrites it.
 * 2. ATTRIBUTION. These figures once carried a "Work our team has led" label,
 *    because the source described them as "a mix of WeAreQA engagements and
 *    work your team led elsewhere". With the Communications copy the client
 *    confirmed WeAreQA attribution for all five case studies they come from, so
 *    the label was retired and the hub now matches the pages it links to.
 * 3. ROUTES. All five industry pages are built and wired.
 *
 * The pharma scope exclusion that once sat in §4 has been removed:
 * /industries/pharma answers it — WeAreQA does take on CSV lifecycle and GAMP 5
 * categorisation, so "Pharma engagements beyond Part 11" was no longer true.
 */

export const seo = {
  title: "QA & Software Testing Services by Industry",
  description:
    'Quality engineering for fintech, healthcare, pharma, e-commerce and SaaS, and communications. What "done" means depends on what happens when you fail.',
} as const;

export const hero = {
  heading: "Industries",
  thesis: '"Quality" means is defined by what happens when you fail.',
  thesisLead: "What ",
  thesisAccent: '"quality"',
  thesisTail: " means is defined by what happens when you fail.",
  body: [
    "A bad release costs revenue per minute in e-commerce. In fintech it costs money and then a regulator asks why. In pharma it becomes a finding in an inspection. Same engineering discipline, entirely different risk calculus, evidence requirements, and definition of done.",
    "That difference is not a matter of domain vocabulary. It changes what you test first, how much evidence you keep, and who signs off.",
  ],
} as const;

import type { IconName } from "../../components/ui/waq/Icon";

export type IndustryCard = {
  name: string;
  icon: IconName;
  /** Intended path. Held at "#" until the page exists. */
  route: string;
  href: string;
  linkLabel: string;
  failure: string;
  demands: string;
  metrics: { value: string; label: string }[] | null;
  /** Pharma has no case study; it carries a stated scope and limit instead. */
  statement: string[] | null;
};

export const industries: IndustryCard[] = [
  {
    name: "Fintech & Financial Services",
    icon: "cost",
    route: "/industries/fintech",
    href: "/industries/fintech",
    linkLabel: "Fintech quality engineering",
    failure:
      "Money moves incorrectly, and someone with statutory authority asks you to explain how.",
    demands:
      "Transaction and reconciliation coverage, data integrity under concurrency, audit trails that survive review, and evidence produced as a by-product of testing rather than assembled afterward.",
    metrics: [
      { value: "60%", label: "faster test design" },
      { value: "35%", label: "more coverage" },
      { value: "40%", label: "less manual effort" },
    ],
    statement: null,
  },
  {
    name: "Healthcare & HealthTech",
    icon: "pulse",
    route: "/industries/healthcare",
    href: "/industries/healthcare",
    linkLabel: "Healthcare quality engineering",
    failure:
      "Clinical decisions get made on data that isn't right, and the failure is invisible until it isn't.",
    demands:
      "Requirement traceability from clinical intent to test result, PHI handling under HIPAA, and interoperability testing across systems you don't control.",
    metrics: [
      { value: "50%", label: "faster requirement analysis" },
      { value: "45%", label: "fewer requirement defects" },
      { value: "100%", label: "traceability" },
    ],
    statement: null,
  },
  {
    name: "Pharma",
    icon: "flask",
    route: "/industries/pharma",
    href: "/industries/pharma",
    linkLabel: "Pharma validation",
    failure:
      "Data integrity is challenged during an inspection, and remediation costs more than the original project.",
    demands:
      "Validation under 21 CFR Part 11 — electronic records, electronic signatures, audit trails, and ALCOA+ data integrity, with documentation that stands up to an auditor rather than a stakeholder.",
    metrics: null,
    statement: [
      "We validate pharma software products against 21 CFR Part 11.",
      "Part 11 is our scope — electronic records, signatures, audit trail, data integrity. We'll tell you plainly where a programme needs capability we don't have rather than learn it on your validation.",
    ],
  },
  {
    name: "E-commerce & SaaS",
    icon: "cart",
    route: "/industries/ecommerce",
    href: "/industries/ecommerce",
    linkLabel: "E-commerce & SaaS quality engineering",
    failure:
      "Revenue stops, and conversion dies quietly for hours before anyone notices.",
    demands:
      "Checkout and subscription path coverage, behaviour under peak load, third-party integrations you can't control, and release confidence at a cadence that doesn't allow a two-week regression cycle.",
    metrics: [
      { value: "90%", label: "accuracy in release-readiness prediction" },
      { value: "90%", label: "time saved on reporting" },
    ],
    statement: null,
  },
  {
    name: "Communications",
    icon: "broadcast",
    route: "/industries/communications",
    href: "/industries/communications",
    linkLabel: "Communications quality engineering",
    failure:
      "The defect is service-affecting, it reaches a large population at once, and the same class of defect returns next quarter.",
    demands:
      "Root cause analysis that actually closes the loop, defect trend intelligence, and testing across a stack where new services sit on infrastructure older than the team.",
    metrics: [
      { value: "55%", label: "faster root cause identification" },
      { value: "40%", label: "reduction in repeat defects" },
    ],
    statement: null,
  },
];

export const constant = {
  heading: "Does QA differ by industry?",
  lead: "The risk model changes. The engineering doesn't.",
  bodyLead: "Every engagement runs on the same operating model — the ",
  frameworkLink: {
    label: "AI-Native Delivery Framework",
    href: "/services/ai-driven-validation#framework-heading",
  },
  bodyMid: " — and is measured against the same ",
  maturityLink: { label: "AI Maturity Model", href: "/#assessment" },
  bodyTail:
    ". What the industry determines is where you start, what evidence you retain, and which failures are unacceptable rather than merely expensive.",
  close: "A vendor whose method changes completely by sector doesn't have a method.",
} as const;

export const notFit = {
  heading: "Where we're not the right fit",
  items: [
    {
      lead: "Safety-critical embedded systems.",
      tail: " DO-178C and ISO 26262 programmes need specialists we aren't.",
      gap: null,
    },
    {
      lead: "Volume staffing.",
      tail: " If you need twenty testers next month, we're a small senior team and we'll say so on the first call.",
      gap: null,
    },
  ],
  close:
    "Telling you what we're wrong for is cheaper for both of us than discovering it in month two.",
} as const;

/**
 * One paragraph in the source. Split at its own sentence boundary so the closing
 * question carries the section the way the heading does on every other CTA —
 * rendering only, not a wording change.
 */
export const cta = {
  question: "Not sure which of these describes you, or you're in none of them?",
  answerLead: "The engineering is the same. ",
  linkLabel: "Book a call",
  tail: " and we'll tell you honestly whether we're a fit.",
} as const;
